<template>
  <div class="checkbox-holder">
    <input
      type="checkbox"
      :id="id"
      :data-qa="dataQa"
      :tabindex="tabindex || '0'"
      @input="handleInput($event)"
      @keydown.enter="
        ($event.target as HTMLInputElement).checked = !($event.target as HTMLInputElement).checked;
        handleInput($event);
      "
      :class="['rbz-checkbox', 'checkbox-style', classes]"
      :checked="checked"
      :indeterminate="indeterminate"
      :disabled="isDisabled"
    />
    <label :for="id" v-if="label" class="label-class">
      {{ label }}
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface InputButton {
  id: string;
  indeterminate?: boolean;
  checked?: boolean;
  label?: string;
  disabled?: boolean;
  isUpdateInProcess?: boolean;
  ignoreUpdateInProcess?: boolean;
  classes?: string;
  dataQa?: string;
  tabindex?: string;
}

const props = withDefaults(defineProps<InputButton>(), {
  checked: false,
  indeterminate: false,
  label: '',
  disabled: false,
  classes: '',
  dataQa: '',
  tabindex: '0',
});

interface FormCheckboxEmits {
  (e: 'update:checkedValue', value: boolean): void;
}

const emit = defineEmits<FormCheckboxEmits>();

const isDisabled = computed(() => {
  return props.disabled || (props.isUpdateInProcess && !props.ignoreUpdateInProcess);
});

const handleInput = (event: Event) => {
  emit('update:checkedValue', (event.target as HTMLInputElement).checked);
  clearFocus(event);
};

const clearFocus = (event: Event) => {
  if (event.pointerType === 'mouse') {
    (event.currentTarget as HTMLElement)?.blur();
  }
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.checkbox-holder {
  display: inline-block;

  .label-class {
    margin-left: 8px;
  }

  .label-class,
  .checkbox-style {
    color: map.get(colors.$colors, 'dusty-gray');
    display: inline-block;
    vertical-align: middle;
  }

  .checkbox-style {
    appearance: none;
    background-color: map.get(colors.$colors, 'white');
    border: 1.5px solid map.get(colors.$colors, 'dusty-gray');
    border-radius: 2px;
    cursor: pointer;
    display: inline-block;
    height: 16px;
    margin: 0;
    position: relative;
    width: 16px;
  }

  .checkbox-style::after {
    border-bottom: 1px solid transparent;
    border-right: 1px solid transparent;
    content: '';
    display: inline-block;
    height: 5px;
    left: 4px;
    position: absolute;
    top: -3px;
    transform: rotate(45deg);
    width: 3px;
  }

  .checkbox-style:checked {
    background-color: map.get(colors.$colors, 'jewel');
    border: 2px solid transparent;
    outline: none;
  }

  .checkbox-style:checked::after {
    border-bottom: 2px solid #fff;
    border-right: 2px solid #fff;
    box-sizing: border-box;
    content: '';
    display: inline-block;
    height: 12px;
    left: 3px;
    position: absolute;
    top: -1px;
    transform: rotate(43deg);
    width: 6px;
  }

  .checkbox-style:indeterminate {
    background-color: map.get(colors.$colors, 'jewel');
    border: 2px solid transparent;
  }

  .checkbox-style:indeterminate::after {
    border: 1px solid #fff;
    box-sizing: border-box;
    content: '';
    display: inline-block;
    height: 0;
    left: 2px;
    position: absolute;
    top: 5px;
    transform: rotate(0deg);
    width: 8px;
  }

  .checkbox-style:focus,
  .checkbox-style:active {
    outline: none;
  }

  .checkbox-style:active {
    background-color: map.get(colors.$colors, 'gallery');
    border: 2px solid map.get(colors.$colors, 'shuttle-gray');
  }

  .checkbox-style:active + .label-class {
    color: map.get(colors.$colors, 'shuttle-gray');
    outline: none;
  }

  .checkbox-style:hover {
    background-color: map.get(colors.$colors, 'gallery');
    border: 2px solid map.get(colors.$colors, 'dusty-gray');
  }

  .checkbox-style:focus:not(:active) {
    background-color: map.get(colors.$colors, 'white');
    border: 2px solid map.get(colors.$colors, 'dusty-gray');
    border-radius: 2px;
    box-shadow: 0 0 0 2px map.get(colors.$colors, 'magic-mint');
  }

  .checkbox-style:disabled {
    background-color: map.get(colors.$colors, 'white');
    border: 2px solid map.get(colors.$colors, 'silver');
    border-radius: 2px;
    pointer-events: none;
  }

  .checkbox-style:disabled + .label-class {
    color: map.get(colors.$colors, 'silver');
  }

  .checkbox-style:checked:hover,
  .checkbox-style:indeterminate:hover {
    background-color: map.get(colors.$colors, 'mountain-meadow-light');
    border: 2px solid transparent;
  }

  .checkbox-style:checked:active,
  .checkbox-style:indeterminate:active {
    background-color: map.get(colors.$colors, 'stratos');
    border: 2px solid transparent;
  }

  .checkbox-style:checked:focus:not(:active),
  .checkbox-style:indeterminate:focus:not(:active) {
    background-color: map.get(colors.$colors, 'jewel');
    border: 2px solid map.get(colors.$colors, 'jewel');
  }

  &:has(input:disabled) {
    pointer-events: none;
  }

  .checkbox-style:checked:disabled,
  .checkbox-style:indeterminate:disabled {
    opacity: 0.3;
  }
}
</style>
